import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Plus, Minus, Trash2 } from "lucide-react";
import type { Product, OrderWithItems } from "@shared/schema";

interface CartItem {
  productId: string;
  quantity: number;
  product?: Product;
}

interface EditOrderDialogProps {
  order: OrderWithItems | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function EditOrderDialog({ order, open, onOpenChange }: EditOrderDialogProps) {
  const { toast } = useToast();
  const [cart, setCart] = useState<CartItem[]>([]);

  const { data: products, isLoading: productsLoading } = useQuery<Product[]>({
    queryKey: ["/api/admin/products"],
  });

  useEffect(() => {
    if (order?.items) {
      setCart(
        order.items.map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
          product: item.product,
        }))
      );
    }
  }, [order]);

  const updateOrderMutation = useMutation({
    mutationFn: (data: { items: { productId: string; quantity: number }[] }) =>
      apiRequest("PATCH", `/api/admin/orders/${order?.id}`, data),
    onSuccess: () => {
      toast({ title: "Order uppdaterad!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/orders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
      onOpenChange(false);
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte uppdatera ordern.", variant: "destructive" });
    },
  });

  const updateQuantity = (productId: string, delta: number) => {
    setCart((prev) =>
      prev
        .map((item) =>
          item.productId === productId
            ? { ...item, quantity: Math.max(0, item.quantity + delta) }
            : item
        )
        .filter((item) => item.quantity > 0)
    );
  };

  const addProduct = (product: Product) => {
    const existing = cart.find((item) => item.productId === product.id);
    if (existing) {
      updateQuantity(product.id, 1);
    } else {
      setCart((prev) => [...prev, { productId: product.id, quantity: 1, product }]);
    }
  };

  const removeProduct = (productId: string) => {
    setCart((prev) => prev.filter((item) => item.productId !== productId));
  };

  const handleSubmit = () => {
    if (cart.length === 0) {
      toast({ title: "Fel", description: "Lägg till minst en produkt.", variant: "destructive" });
      return;
    }

    updateOrderMutation.mutate({
      items: cart.map((item) => ({ productId: item.productId, quantity: item.quantity })),
    });
  };

  const getUnitLabel = (unit: string) => {
    switch (unit) {
      case "pcs": return "st";
      case "bag": return "påse";
      case "box": return "låda";
      default: return unit;
    }
  };

  const availableProducts = products?.filter(
    (p) => p.isActive && !cart.some((item) => item.productId === p.id)
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Redigera order #{order?.orderNumber}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div>
            <Label className="text-base font-semibold">Kund</Label>
            <p className="text-muted-foreground">{order?.client?.name}</p>
          </div>

          <div>
            <Label className="text-base font-semibold mb-3 block">Produkter i ordern</Label>
            {cart.length === 0 ? (
              <p className="text-sm text-muted-foreground">Inga produkter tillagda</p>
            ) : (
              <div className="space-y-2">
                {cart.map((item) => (
                  <div
                    key={item.productId}
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    <div>
                      <p className="font-medium">{item.product?.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {item.product?.unitPrice} kr / {getUnitLabel(item.product?.sellUnit || "pcs")}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => updateQuantity(item.productId, -1)}
                        data-testid={`button-decrease-${item.productId}`}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span className="w-8 text-center font-medium">{item.quantity}</span>
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => updateQuantity(item.productId, 1)}
                        data-testid={`button-increase-${item.productId}`}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => removeProduct(item.productId)}
                        className="text-destructive"
                        data-testid={`button-remove-${item.productId}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {availableProducts && availableProducts.length > 0 && (
            <div>
              <Label className="text-base font-semibold mb-3 block">Lägg till produkt</Label>
              <div className="grid grid-cols-2 gap-2">
                {productsLoading ? (
                  [1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-10 w-full" />)
                ) : (
                  availableProducts.map((product) => (
                    <Button
                      key={product.id}
                      variant="outline"
                      className="justify-start"
                      onClick={() => addProduct(product)}
                      data-testid={`button-add-product-${product.id}`}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      {product.name}
                    </Button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Avbryt
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={updateOrderMutation.isPending || cart.length === 0}
            data-testid="button-save-order"
          >
            {updateOrderMutation.isPending ? "Sparar..." : "Spara ändringar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
